import React, { useMemo } from 'react';
import { Box, Card, CardContent, Divider, Skeleton, Typography } from '@mui/material';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip as ReTooltip, Cell } from 'recharts';
import { formatMoneyShort, formatInt } from 'src/utils/format';
import { EmptyContent } from 'src/components/empty-content';
import { useTranslate } from 'react-admin';

export type CoinDistItem = { coin: string; symbol: string; base: string; volume: number; users: number };

type CoinRow = CoinDistItem & { __pct?: number; __placeholder?: boolean };

function tooltipStyle() {
  return {
    minWidth: 180,
    background: 'rgba(0,0,0,0.61)',
    border: 'none',
    borderRadius: 12,
    fontSize: 12,
    color: '#fff',
    padding: '10px 12px',
    fontWeight: 400,
  } as const;
}

function CoinTooltip({ active, payload, t }: any) {
  if (!active || !payload?.length) return null;

  const row = payload[0].payload as CoinRow;
  if (!row || row.__placeholder) return null;

  return (
    <div style={tooltipStyle()}>
      <div style={{ fontWeight: 500, marginBottom: 6 }}>{row.symbol}</div>

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
        <span>{t('module.dashboardV2.coinDist.tooltip.pctOfTotalVol', { _: '% of Total Vol' })}</span>
        <span>{Math.round((row.__pct ?? 0) * 10) / 10}%</span>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, marginTop: 4 }}>
        <span>{t('module.dashboardV2.coinDist.tooltip.volume', { _: 'Volume' })}</span>
        <span>{formatMoneyShort(Number(row.volume))}</span>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, marginTop: 4 }}>
        <span>{t('module.dashboardV2.coinDist.tooltip.users', { _: 'Users' })}</span>
        <span>{formatInt(Number(row.users))}</span>
      </div>
    </div>
  );
}

const SLOT_COUNT = 7;
const BAR_SIZE = 40;

export function CoinDistributionCard({
  title,
  data,
  loading,
}: {
  title: string;
  data: CoinDistItem[];
  loading?: boolean;
}) {
  const t = useTranslate();

  const rows = useMemo<CoinRow[]>(() => {
    const safe = (data ?? []).filter((d) => d && d.coin);

    const total = safe.reduce((s, d) => s + (d.volume || 0), 0) || 1;
    const enriched: CoinRow[] = safe.map((d) => ({
      ...d,
      __pct: ((d.volume || 0) / total) * 100,
      __placeholder: false,
    }));

    const fillCount = Math.max(0, SLOT_COUNT - enriched.length);
    for (let i = 0; i < fillCount; i += 1) {
      enriched.push({
        coin: '',
        symbol: '',
        base: '',
        volume: 0,
        users: 0,
        __pct: 0,
        __placeholder: true,
      });
    }

    return enriched.slice(0, SLOT_COUNT);
  }, [data]);

  const hasRealData = useMemo(
    () => (data?.some((d) => (d.volume ?? 0) > 0 || (d.users ?? 0) > 0) ?? false),
    [data]
  );

  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
          {title}
        </Typography>
        <Divider sx={{ my: 2 }} />

        <Box sx={{ height: 450 }}>
          {loading ? (
            <Skeleton variant="rounded" height={450} />
          ) : !hasRealData ? (
            <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <EmptyContent title={t('common.noData', { _: 'No data' })} />
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={rows}
                margin={{ top: 10, right: 10, bottom: 10, left: 0 }}
                barCategoryGap={24}
                barGap={6}
              >
                <XAxis dataKey="coin" tick={{ fontSize: 12 }} interval={0} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => formatMoneyShort(Number(v ?? 0))} />

                <ReTooltip content={(props) => <CoinTooltip {...props} t={t} />} cursor={{ fill: 'transparent' }} />

                <Bar
                  dataKey="volume"
                  radius={[8, 8, 0, 0]}
                  barSize={BAR_SIZE}
                  minPointSize={0}
                  isAnimationActive={false}
                >
                  {rows.map((r, idx) => (
                    <Cell key={idx} fill={r.__placeholder ? 'rgba(0,0,0,0)' : '#000000'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}